<template>
    <v-form ref="form" v-model="valid" data-cy="campaign-form">
        <v-row dense>
            <v-col cols="12" sm="6" md="2">
                <LocaleSwitcher @change="changeActiveLangCode" />
            </v-col>
            <v-col cols="12" sm="6" md="6" class="align-center">
                <v-switch
                    v-model="campaign.status"
                    color="primary"
                    class="ml-auto width-max-content pt-2"
                    :label="campaign.status ? $t('active') : $t('inactive')"
                    data-cy="company-status"
                    hide-details
                />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" sm="6" md="4">
                <TextFieldLocale
                    v-model="campaign.name[activeLangCode]"
                    :label="`${$t('name')}*`"
                    :rules="[requiredRule('name')]"
                    :active-lang-code="activeLangCode"
                    data-cy="campaign-name"
                />
            </v-col>
            <v-col cols="12" sm="6" md="4">
                <v-autocomplete
                    v-model="campaign.assignedUser"
                    item-title="name"
                    item-value="personId"
                    :items="users"
                    :label="$t('assigned_to')"
                    :loading="usersLoading"
                    :disabled="usersLoading"
                    clearable
                    variant="outlined"
                    color="primary"
                    data-cy="assigned-user"
                />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" sm="6" md="4">
                <v-select
                    v-model="campaign.type"
                    :items="campaignTypeItems"
                    :label="$t('campaign_type')"
                    clearable
                    variant="outlined"
                    color="primary"
                    data-cy="campaign-type"
                />
            </v-col>
            <v-col cols="12" sm="3" md="2">
                <v-text-field
                    v-model="campaign.budgetValue"
                    :label="$t('budget')"
                    type="number"
                    hide-spin-buttons
                    variant="outlined"
                    color="primary"
                    data-cy="budget-value"
                />
            </v-col>
            <v-col>
                <v-select
                    v-model="campaign.budgetCurrency"
                    :items="currencyItems"
                    :label="$t('currency')"
                    clearable
                    variant="outlined"
                    color="primary"
                    data-cy="budget-currency"
                    class="width-max-content"
                    min-width="100"
                />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" sm="6" md="4">
                <daterange
                    :start-date-label="`${$t('date_from')}*`"
                    :end-date-label="`${$t('date_to')}*`"
                    :single-line="false"
                    :hide-details="false"
                    :min-start-date="null"
                    same-date
                    :_start-date="campaign.dateFrom"
                    :_end-date="campaign.dateTo"
                    :rules="[requiredRule('date')]"
                    data-cy="date-range"
                    @change-start-date="campaign.dateFrom = $event"
                    @change-end-date="campaign.dateTo = $event"
                />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" md="8">
                <h4 class="text-h6 text-capitalize">
                    {{ $t('comment') }}
                </h4>
                <div class="px-0 font-italic text-subtitle-2">
                    {{ $t('enter_comment') }}
                </div>
                <TextareaLocale v-model="campaign.description[activeLangCode]" data-cy="comment" rows="4" />
            </v-col>
        </v-row>
        <v-row no-gutters class="gc-4">
            <v-btn :loading="isLoading" :disabled="isLoading || !valid" data-cy="save" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn data-cy="cancel" :to="{name: 'campaigns'}" variant="text">
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script lang="ts">
    import {Component, mixins, Prop, Provide} from 'vue-facing-decorator'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import type {SelectOptionType} from '~/utils/types/types'
    import Daterange from '@/components/search/forms/Daterange.vue'
    import {clean, clone, formatDateToISO} from '~/utils/helpers'
    import {initCampaign} from '~/utils/types/account/campaigns/helpers'
    import TextFieldLocale from '~/components/TextFieldLocale.vue'

    @Component({components: {TextFieldLocale, Daterange}})
    export class CampaignForm extends mixins(FormValidationRulesMixin) {
        @Prop({default: null}) campaignData
        @Provide
        activeLangCode: string = 'en'

        isLoading: boolean = false
        valid: boolean = false
        usersLoading: boolean = false
        users = []
        campaign = initCampaign()

        async mounted() {
            this.activeLangCode = this.$i18n.locale
            try {
                this.usersLoading = true
                const {users} = await this.$api.persons.get({companyId: authStore.company.id})
                this.users = users
                    .map(({personId, firstName, lastName}) => ({
                        personId,
                        name: `${lastName || ''} ${firstName || ''}`.trim(),
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name))
            } catch (e) {
                this.users = []
            } finally {
                this.usersLoading = false
            }
            this.campaign = clone(this.campaignData)
            if (this.campaign.dateFrom) this.campaign.dateFrom = new Date(this.campaign.dateFrom)
            if (this.campaign.dateTo) this.campaign.dateTo = new Date(this.campaign.dateTo)
        }

        async save(): Promise<void> {
            const {name, description, dateFrom, dateTo, assignedUser, type, budgetValue, budgetCurrency, status} =
                this.campaign
            let campaign = {
                name,
                description,
                campaignDateFrom: dateFrom ? formatDateToISO(dateFrom) : null,
                campaignDateTo: dateTo ? formatDateToISO(dateTo) : null,
                assignedUserId: null,
                type,
                budgetValue,
                budgetCurrency,
                status,
            }
            if (assignedUser) {
                campaign.assignedUserId = typeof assignedUser === 'number' ? assignedUser : assignedUser?.id
            }
            clean(campaign)

            const routId = this.$route.params.id

            try {
                this.isLoading = true
                if (routId === '0') {
                    await this.$api.campaigns.post(campaign)
                } else {
                    await this.$api.campaigns.put(routId, campaign)
                }
                this.$toast.success(this.$t('messages.success.campaign_saved'))
                this.$router.back()
            } catch (e) {
                this.$toast.error(this.$t('data_update_fail'))
            } finally {
                this.isLoading = false
            }
        }

        changeActiveLangCode(code) {
            this.activeLangCode = code
        }

        get campaignTypeItems(): SelectOptionType[] {
            const campaignTypes = ['EXHIBISION', 'ROAD_SHOW', 'ADVERTISEMENT', 'OTHER']
            return campaignTypes.map(value => ({value, title: this.$t(`campaign_types.${value}`)}))
        }

        get currencyItems(): SelectOptionType[] {
            const currencies = Object.values(this.$conf.public.currency.available).filter(currency =>
                Object.keys(runtimeStore.exchangeRates).includes(currency)
            )
            return currencies.map(value => ({value, title: value}))
        }
    }

    export default CampaignForm
</script>
